import { useEffect, useRef, useState } from 'react'

interface AnnouncementVideoPlayerProps {
  src: string
  className?: string
}

/**
 * Lightweight video player for announcement media.
 *
 * Announcement videos are uploaded directly to Bunny CDN Storage as MP4/MOV
 * files — not to Bunny Stream — so there is no HLS playlist and no tus upload
 * flow. A standard <video> element with controls is all that is needed.
 *
 * The component generates a local poster frame from the video so the card
 * shows a meaningful thumbnail before the user presses play, matching the
 * visual style of image announcements.
 */
const AnnouncementVideoPlayer = ({ src, className }: AnnouncementVideoPlayerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [poster, setPoster] = useState('')

  // Generate a poster by seeking to 1 second (or the midpoint for short clips)
  // and capturing a canvas frame. Falls back silently if CORS blocks the export.
  useEffect(() => {
    if (!src) return

    let cancelled = false
    const video = document.createElement('video')
    video.crossOrigin = 'anonymous'
    video.muted = true
    video.playsInline = true
    video.preload = 'metadata'
    video.src = src

    const capture = () => {
      if (cancelled) return
      try {
        const seekTo = Number.isFinite(video.duration) && video.duration > 1 ? Math.min(1, video.duration * 0.1) : 0

        video.currentTime = seekTo
      } catch {
        // duration not available yet — will retry on seeked
      }
    }

    const onSeeked = () => {
      if (cancelled || !video.videoWidth || !video.videoHeight) return
      try {
        const canvas = document.createElement('canvas')
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        const ctx = canvas.getContext('2d')
        if (!ctx) return
        ctx.drawImage(video, 0, 0)
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8)
        if (!cancelled) setPoster(dataUrl)
      } catch {
        // CORS blocked canvas export — leave poster empty
      } finally {
        video.removeAttribute('src')
        video.load()
      }
    }

    video.addEventListener('loadedmetadata', capture, { once: true })
    video.addEventListener('seeked', onSeeked, { once: true })
    video.addEventListener(
      'error',
      () => {
        video.removeAttribute('src')
      },
      { once: true },
    )
    video.load()

    return () => {
      cancelled = true
      video.removeAttribute('src')
      video.load()
    }
  }, [src])

  return (
    <video
      ref={videoRef}
      src={src}
      controls
      playsInline
      preload="metadata"
      poster={poster}
      className={className}
      style={{ width: '100%', display: 'block', borderRadius: '8px' }}
    />
  )
}

export default AnnouncementVideoPlayer
